import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Input,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr
} from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'

import { JobPositionService } from '../../../services/JobPosition/JobPositionService'
import { JobPosition } from '../../../services/JobPosition/types'
import { JobPositionDeleteDialog } from './JobPositionDeleteDialog'
import { JobPositionFormModal } from './JobPositionFormModal'

const statusColors = {
  active: 'green',
  paused: 'red'
}

const statusTranslations = {
  active: 'Ativa',
  paused: 'Pausada'
}

export default function JobPositionPage() {
  const service = new JobPositionService()
  const ref = useRef()
  const [jobPositions, setJobPositions] = useState<JobPosition[]>([])
  const [jobPositionsFiltered, setJobPositionsFiltered] = useState<JobPosition[]>([])
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [targetRecord, setTargetRecord] = useState<JobPosition>()
  const [search, setSearch] = useState('')
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [recordEditing, setRecordEditing] = useState<JobPosition>()

  useEffect(() => {
    refreshList()
  }, [])

  useEffect(() => {
    setJobPositionsFiltered(
      jobPositions.filter((jobPosition) => jobPosition.name.toLowerCase().includes(search.toLowerCase()))
    )
  }, [jobPositions, search])

  const refreshList = async () => {
    service.list().then((response) => {
      setSearch('')
      setJobPositions(response)
    })
  }

  const handleStatusChange = (jobPosition: JobPosition, status: 'active' | 'paused') => {
    service.updateStatus(jobPosition.id, status).then((response) => {
      setJobPositions((prev) => prev.map((jobPosition) => (jobPosition.id === response.id ? response : jobPosition)))
    })
  }

  return (
    <>
      <Flex alignItems="center" justifyContent="space-between">
        <Heading as="h1" size="lg" marginBottom="5" mb="10" textTransform="uppercase" w="35%">
          Vagas disponíveis
        </Heading>
      </Flex>
      <Box width="30%" ml="auto" mb="5">
        <Input
          placeholder="Digite para pesquisar"
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />
      </Box>
      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>Vaga</Th>
              <Th>Áreas</Th>
              <Th>Status</Th>
              <Th>Criada por</Th>
              <Th>Criada em</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {jobPositions.length === 0 ? (
              <Tr>
                <Td colSpan={6} textAlign="center">
                  Nenhuma vaga cadastrada
                </Td>
              </Tr>
            ) : jobPositionsFiltered.length === 0 ? (
              <Tr>
                <Td colSpan={6} textAlign="center">
                  Nenhuma vaga encontrada
                </Td>
              </Tr>
            ) : (
              jobPositionsFiltered.map((jobPosition) => (
                <Tr key={jobPosition.id}>
                  <Td>{jobPosition.name}</Td>
                  <Td>
                    <Badge colorScheme={statusColors[jobPosition.status]}>
                      {statusTranslations[jobPosition.status]}
                    </Badge>
                  </Td>
                  <Td>
                    {jobPosition.areas == null || jobPosition.areas.length == 0 ? (
                      <small className="text-muted">Sem áreas</small>
                    ) : (
                      <Tooltip
                        label={jobPosition.areas.map((area) => (
                          <Text key={area}>- {area}</Text>
                        ))}
                        hasArrow
                      >
                        <Text>{jobPosition.areas.length} áreas</Text>
                      </Tooltip>
                    )}
                  </Td>
                  <Td>{jobPosition.creator.name}</Td>
                  <Td>{new Date(jobPosition.created_at).toLocaleString('pt-BR').replace(',', ' ')}</Td>
                  <Td>
                    {jobPosition.status === 'active' ? (
                      <Tooltip label="Pausar" hasArrow placement="top">
                        <IconButton
                          aria-label="Pausar"
                          colorScheme="orange"
                          size="sm"
                          mr={2}
                          onClick={() => handleStatusChange(jobPosition, 'paused')}
                        >
                          <i className="lni lni-pause"></i>
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip label="Ativar" hasArrow placement="top">
                        <IconButton
                          aria-label="Ativar"
                          colorScheme="green"
                          size="sm"
                          mr={2}
                          onClick={() => handleStatusChange(jobPosition, 'active')}
                        >
                          <i className="lni lni-play"></i>
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip label="Editar" hasArrow placement="top">
                      <IconButton
                        aria-label="Editar"
                        colorScheme="blue"
                        size="sm"
                        mr={2}
                        onClick={() => {
                          setRecordEditing(jobPosition)
                          setIsCreateModalOpen(true)
                        }}
                      >
                        <i className="lni lni-pencil"></i>
                      </IconButton>
                    </Tooltip>
                    <Tooltip label="Excluir" hasArrow placement="top">
                      <IconButton
                        aria-label="Excluir"
                        colorScheme="red"
                        size="sm"
                        mr={2}
                        onClick={() => {
                          setTargetRecord(jobPosition)
                          setOpenDeleteDialog(true)
                        }}
                      >
                        <i className="lni lni-trash-can"></i>
                      </IconButton>
                    </Tooltip>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>

      {targetRecord && (
        <JobPositionDeleteDialog
          isOpen={openDeleteDialog}
          jobPosition={targetRecord}
          leastDestructiveRef={ref}
          onClose={() => {
            setOpenDeleteDialog(false)
            refreshList()
          }}
        />
      )}

      <Tooltip label="Adicionar manualmente" aria-label="Adicionar manualmente" placement="left">
        <Button
          colorScheme="magenta"
          position="fixed"
          bottom="20px"
          right="20px"
          borderRadius="full"
          width="70px"
          height="70px"
          onClick={() => {
            setRecordEditing(undefined)
            setIsCreateModalOpen(true)
          }}
        >
          <i className="lni lni-plus"></i>
        </Button>
      </Tooltip>

      <JobPositionFormModal
        isOpen={isCreateModalOpen}
        jobPosition={recordEditing}
        onClose={() => {
          refreshList()
          setIsCreateModalOpen(false)
        }}
      />
    </>
  )
}
